import { useEffect, useState } from "react";
import { FaSortDown, FaSortUp } from "react-icons/fa";
import { useParams } from "react-router-dom";
import { Plus, ArrowUpDown } from "lucide-react";
import { TokenSelector } from "../components/TokenSelector";
import GradientButton from "../UIComponents/GradientButton";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../redux/store";
import { getWalletList } from "../redux/slices/walletSlice";
import { createConvert } from "../redux/slices/historySlice";
import { toast, ToastContainer } from "react-toastify";

interface Token {
  id: string;
  symbol: string;
  name: string;
  image: string;
  balance: number;
  type: string;
  icon: string;
  min: number;
  max: number;
  networks?: {
    tokenType: string;
    contractAddress: string;
  }[];
}

interface ConversionRate {
  [key: string]: number;
}

const useBinancePriceData = () => {
  const [priceData, setPriceData] = useState<Record<string, number>>({});
  const [ws, setWs] = useState<WebSocket | null>(null);

  const connectWebSocket = (symbols: string[]) => {
    if (symbols.length === 0) return;

    // Close previous connection if exists
    if (ws) {
      ws.close();
    }

    const newWs = new WebSocket(`wss://stream.binance.com:9443/ws`);

    newWs.onopen = () => {
      const subscribeMessage = {
        method: "SUBSCRIBE",
        params: symbols.map((sym) => `${sym.toLowerCase()}@ticker`),
        id: 1,
      };
      newWs.send(JSON.stringify(subscribeMessage));
    };

    newWs.onmessage = (event) => {
      const data = JSON.parse(event.data);
      if (data.s && data.c) {
        // Symbol and current price
        setPriceData((prev) => ({
          ...prev,
          [data.s.replace("USDT", "")]: parseFloat(data.c),
        }));
      }
    };

    newWs.onerror = (error) => {
      console.error("WebSocket error:", error);
    };

    newWs.onclose = () => {
      console.log("WebSocket disconnected");
    };

    setWs(newWs);
  };

  useEffect(() => {
    return () => {
      if (ws) {
        ws.close();
      }
    };
  }, [ws]);

  return { priceData, connectWebSocket };
};

const Convert: React.FC = () => {
  const [fromToken, setFromToken] = useState<Token | null>(null);
  const [toToken, setToToken] = useState<Token | null>(null);

  const [fromAmount, setFromAmount] = useState<string>("");
  const [toAmount, setToAmount] = useState<string>("");
  const [conversionRate, setConversionRate] = useState<number>(0);
  const [isConverting, setIsConverting] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [isLoadingRates, setIsLoadingRates] = useState<boolean>(true);

  const dispatch = useDispatch<AppDispatch>();
  const { wallet, isLoading } = useSelector((state: any) => state.wallet);
  const { priceData, connectWebSocket } = useBinancePriceData();
  const [openSelector, setOpenSelector] = useState<"from" | "to" | null>(null);

  const handleToggle = (type: "from" | "to") => {
    setOpenSelector((prev) => (prev === type ? null : type));
  };

  // Initialize with default tokens if available
  useEffect(() => {
    if (wallet?.result?.length > 0) {
      const usdc = wallet.result.find((t: Token) => t.symbol === "USDC");
      const btc = wallet.result.find((t: Token) => t.symbol === "BTC");

      if (usdc && usdc.balance > 0) setFromToken(usdc);
      if (btc) setToToken(btc);
    }
  }, [wallet]);

  // Fetch wallet data
  useEffect(() => {
    dispatch(getWalletList({}) as any);
  }, [dispatch]);

  // Setup WebSocket connection when tokens change
  useEffect(() => {
    if (wallet?.result?.length > 0) {
      const symbols = wallet.result
        .filter((token: Token) => token.symbol !== "USDT") // Exclude USDT itself
        .map((token: Token) => `${token.symbol}USDT`);

      connectWebSocket(symbols);
      setIsLoadingRates(false);
    }
  }, [wallet?.result]);

  // Calculate conversion when inputs change
  useEffect(() => {
    if (
      !fromToken ||
      !toToken ||
      !priceData[fromToken.symbol] ||
      !priceData[toToken.symbol]
    ) {
      setToAmount("");
      setConversionRate(0);
      return;
    }

    if (!fromAmount || isNaN(parseFloat(fromAmount))) {
      setToAmount("");
      return;
    }

    const fromRate = priceData[fromToken.symbol];
    const toRate = priceData[toToken.symbol];

    if (fromRate && toRate) {
      const amount = parseFloat(fromAmount);
      const rate = fromRate / toRate;
      const convertedAmount = amount * rate;

      setConversionRate(rate);
      setToAmount(convertedAmount.toFixed(8));
    }
  }, [fromAmount, fromToken, toToken, priceData]);

  const swapTokens = () => {
    if (!fromToken || !toToken) return;

    const temp = fromToken;
    setFromToken(toToken);
    setToToken(temp);
    setFromAmount(toAmount);
  };

  const handleFromAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    // Allow only numbers and a single decimal point
    if (value === "" || /^\d*\.?\d*$/.test(value)) {
      setFromAmount(value);
    }
  };

  const handleMaxClick = () => {
    if (fromToken) {
      setFromAmount(fromToken.balance.toString());
    }
  };

  const handleFromTokenSelect = (token: Token | null) => {
    if (token && toToken && token.symbol === toToken.symbol) {
      setError("Cannot select same token for both sides");
      return;
    }
    setError("");
    setFromToken(token);
    setFromAmount("");
  };

  const handleToTokenSelect = (token: Token | null) => {
    if (token && fromToken && token.symbol === fromToken.symbol) {
      setError("Cannot select same token for both sides");
      return;
    }
    setError("");
    setToToken(token);
  };

  const handleConvert = async () => {
    if (!fromToken || !toToken || !fromAmount) {
      setError("Please select tokens and enter an amount");
      return;
    }

    if (fromToken.symbol === toToken.symbol) {
      setError("Cannot convert between same tokens");
      return;
    }

    const amount = parseFloat(fromAmount);
    if (isNaN(amount) || amount <= 0) {
      setError("Amount must be greater than 0");
      return;
    }

    if (fromToken.balance < amount) {
      setError("Insufficient balance");
      return;
    }

    setIsConverting(true);
    setError("");

    try {
      let payload = {
        fromCurrency: fromToken.symbol,
        toCurrency: toToken.symbol,
        amount: fromAmount,
      };
      await dispatch(createConvert(payload) as any).then((action: any) => {
        if (action?.payload.success) {
          toast.dismiss()
          toast.success(action.payload.message, {
            position: "top-right",
            autoClose: 3000,
          });

          dispatch(getWalletList({}) as any);
          setFromAmount("");
        } else {
          toast.dismiss()
          toast.error(action.payload, {
            position: "top-right",
            autoClose: 3000,
          });
        }
      });
    } catch (err) {
      setError("Conversion failed. Please try again.");
    } finally {
      setIsConverting(false);
    }
  };

  const tokens = wallet?.result || [];

  return (
    <div className="dark:text-white rounded-lg mt-11 md:mt-auto">
      <ToastContainer />
      <h2 className="text-[32px] xs:text-[24px] font-delight font-bold mb-10 dark:text-white text-center">
        {fromToken && toToken
          ? `Convert ${fromToken.symbol} to ${toToken.symbol}`
          : "Crypto Exchange"}
      </h2>

      <div className="max-w-md mx-auto mt-10 p-4">
        {/* From Token */}
        <div className="bg-[linear-gradient(265.23deg,_#237249_6.79%,_#35C66B_97.2%)] p-[1.02px] rounded-xl mb-4">
          <div className="rounded-xl p-4 relative bg-white dark:bg-[#0A0B0D] dark:text-white shadow-sm">
            <div className="flex justify-between items-center text-sm text-gray-500 dark:text-gray-400 gap-2">
              <span className="dark:text-white font-medium">From</span>
              {fromToken && (
                <span
                  className="flex items-center gap-1 cursor-pointer text-right hover:underline"
                  onClick={handleMaxClick}
                >
                  Available: {fromToken.balance.toFixed(6)} {fromToken.symbol}
                </span>
              )}
            </div>
            <div className="flex items-center justify-between mt-2 text-xl font-semibold">
              <TokenSelector
                selectedToken={fromToken}
                onSelect={(token: Token | null) => {
                  setFromToken(token);
                  setOpenSelector(null); // close on select
                }}
                tokenList={tokens.filter(
                  (t) => !toToken || t.symbol !== toToken.symbol
                )}
                isOpen={openSelector === "from"}
                onToggle={() => handleToggle("from")}
              />
              <div className="text-right flex-1">
                <input
                  type="text"
                  value={fromAmount}
                  onChange={handleFromAmountChange}
                  className="w-full text-right bg-transparent dark:text-white text-lg font-semibold focus:outline-none"
                  placeholder="0.0"
                  inputMode="decimal"
                  disabled={!fromToken}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Swap Button */}
        <div className="flex justify-center -mt-4 mb-0.5 z-10 relative">
          <button
            className="rounded-full bg-themeColor border p-1 shadow hover:rotate-180 transition-transform"
            onClick={swapTokens}
            disabled={!fromToken || !toToken}
            aria-label="Swap tokens"
          >
            <ArrowUpDown size={20} className="text-white" />
          </button>
        </div>

        {/* To Token */}
        <div className="bg-[linear-gradient(265.23deg,_#237249_6.79%,_#35C66B_97.2%)] p-[1.02px] rounded-xl mb-4">
          <div className="rounded-xl p-4 relative bg-white dark:bg-[#0A0B0D] dark:text-white shadow-sm">
            <div className="flex justify-between items-center text-sm text-gray-500 dark:text-gray-400 gap-2">
              <span className="dark:text-white font-medium">To</span>
              {toToken && (
                <span className="flex items-center gap-1 text-right">
                  Available: {toToken.balance.toFixed(6)} {toToken.symbol}
                </span>
              )}
            </div>
            <div className="flex items-center justify-between mt-2 text-xl font-semibold">
              <TokenSelector
                selectedToken={toToken}
                onSelect={(token: Token | null) => {
                  setToToken(token);
                  setOpenSelector(null); // close on select
                }}
                tokenList={tokens.filter(
                  (t) => !fromToken || t.symbol !== fromToken.symbol
                )}
                isOpen={openSelector === "to"}
                onToggle={() => handleToggle("to")}
              />
              <div className="text-right flex-1">
                <input
                  type="text"
                  value={toAmount}
                  readOnly
                  className="w-full text-right bg-transparent dark:text-white text-lg font-semibold focus:outline-none"
                  placeholder="0.0"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Conversion Info */}
        {fromToken && toToken && conversionRate > 0 && (
          <div className="text-center text-sm text-gray-500 dark:text-gray-400 mb-4">
            <div>
              1 {fromToken.symbol} = {conversionRate.toFixed(8)}{" "}
              {toToken.symbol}
            </div>
            <div>
              1 {toToken.symbol} = {(1 / conversionRate).toFixed(8)}{" "}
              {fromToken.symbol}
            </div>
          </div>
        )}

        {/* Loading state */}
        {isLoadingRates && (
          <div className="text-center text-gray-500 mb-4">Loading rates...</div>
        )}

        {/* Error Message */}
        {error && <div className="text-center text-red-500 mb-4">{error}</div>}

        {/* Convert Button */}
        <GradientButton
          text={isConverting ? "Converting..." : "Convert"}
          onClick={handleConvert}
          disabled={
            isConverting ||
            !fromToken ||
            !toToken ||
            !fromAmount ||
            parseFloat(fromAmount) <= 0 ||
            fromToken.symbol === toToken.symbol ||
            isLoadingRates
          }
          className="mt-6 w-full rounded-lg py-2 h-12"
        />
      </div>
    </div>
  );
};

export default Convert;
